import React, { useState, useEffect } from 'react';
import { TextField, Checkbox } from '@mui/material';

const ContactSelectionModal = ({ contacts = [], initialSelectedContacts = [], onSelectContacts, onClose }) => {
  const [selectedContacts, setSelectedContacts] = useState(initialSelectedContacts);
  const [searchText, setSearchText] = useState('');

  // Update selected contacts when initialSelectedContacts changes
  useEffect(() => {
    setSelectedContacts(initialSelectedContacts);
  }, [initialSelectedContacts]);

  // Helper function
  const getContactDisplayName = (contact) => {
    const firstName = contact.firstName || contact.firstname || '';
    const lastName = contact.lastName || contact.lastname || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || 'Unknown Contact';
  };

  // Filter contacts based on search
  const filteredContacts = contacts.filter(contact => {
    const name = getContactDisplayName(contact).toLowerCase();
    const email = (contact.email || '').toLowerCase();
    const phone = (contact.phone || '').toLowerCase();
    const searchLower = searchText.toLowerCase();
    return name.includes(searchLower) || email.includes(searchLower) || phone.includes(searchLower);
  });

  // Handle contact selection
  const handleContactSelection = (contact) => {
    const isSelected = selectedContacts.some(c => c._id === contact._id);
    if (isSelected) {
      setSelectedContacts(selectedContacts.filter(c => c._id !== contact._id));
    } else {
      setSelectedContacts([...selectedContacts, contact]);
    }
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectedContacts.length === filteredContacts.length && filteredContacts.length > 0) {
      // All are selected, deselect all
      setSelectedContacts([]);
    } else {
      // Select all filtered contacts
      setSelectedContacts(filteredContacts);
    }
  };

  // Handle apply selection
  const handleApplySelection = () => {
    if (selectedContacts.length > 0) {
      onSelectContacts(selectedContacts);
      onClose();
    }
  };

  return (
    <div className='contact-selection-dropdown' style={{ padding: '16px', minWidth: '320px', maxWidth: '400px' }}>
      {/* Header */}
      <div className='heading-area' style={{ marginBottom: '16px' }}>
        <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
          Select Contacts
        </h5>
        {selectedContacts.length > 0 && (
          <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#6b7280' }}>
            {selectedContacts.length} contact{selectedContacts.length !== 1 ? 's' : ''} selected
          </p>
        )}
      </div>

      {/* Select All Button */}
      {filteredContacts.length > 0 && (
        <button 
          className='btn-style-border' 
          onClick={handleSelectAll}
          style={{ 
            width: '100%', 
            marginBottom: '12px', 
            fontSize: '13px',
            padding: '6px 12px'
          }}
        >
          {selectedContacts.length === filteredContacts.length && filteredContacts.length > 0
            ? 'Deselect All'
            : `Select All (${filteredContacts.length})`
          }
        </button>
      )}

      {/* Search Bar */}
      <div className='group-form' style={{ marginBottom: '16px' }}>
        <TextField
          fullWidth
          placeholder="Search contacts..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          size="small"
          sx={{
            '& .MuiInputBase-root': {
              fontSize: '14px'
            }
          }}
        />
      </div>

      {/* Contacts List */}
      <div className='contacts-list' style={{
        maxHeight: '300px',
        overflowY: 'auto',
        marginBottom: '16px'
      }}>
        {filteredContacts.length > 0 ? (
          filteredContacts.map((contact) => {
            const isSelected = selectedContacts.some(c => c._id === contact._id);
            return (
              <div
                key={contact._id}
                className='contact-item'
                onClick={() => handleContactSelection(contact)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px',
                  marginBottom: '8px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  backgroundColor: isSelected ? '#f0f9ff' : 'white',
                  borderColor: isSelected ? '#6366f1' : '#e5e7eb',
                  transition: 'all 0.2s ease'
                }}
              >
                <Checkbox
                  checked={isSelected}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleContactSelection(contact);
                  }}
                  size="small"
                  sx={{ 
                    padding: '4px',
                    marginRight: '4px',
                    marginLeft: '-4px',
                    color: '#6366f1',
                    '&.Mui-checked': {
                      color: '#6366f1'
                    }
                  }}
                />

                <div style={{ flex: 1, minWidth: 0, paddingLeft: '8px' }}>
                  <div style={{ 
                    fontWeight: '600', 
                    fontSize: '14px',
                    color: '#111827',
                    marginBottom: '2px'
                  }}>
                    {getContactDisplayName(contact)}
                  </div>
                  {contact.email && (
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#6b7280',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {contact.email}
                    </div>
                  )}
                  {contact.phone && (
                    <div style={{ 
                      fontSize: '12px', 
                      color: '#9ca3af' 
                    }}>
                      {contact.phone}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '24px',
            color: '#6b7280'
          }}>
            {searchText ? 'No contacts found matching your search' : 'No contacts available'}
          </div>
        )}
      </div>

      {/* Apply Button */}
      {selectedContacts.length > 0 && (
        <button 
          className='btn-style-new' 
          onClick={handleApplySelection}
          style={{ 
            width: '100%', 
            fontSize: '14px',
            padding: '8px 16px'
          }}
        >
          Apply Selection ({selectedContacts.length} contact{selectedContacts.length !== 1 ? 's' : ''})
        </button>
      )}

      <style jsx>{`
        .contact-selection-dropdown {
          background: white;
          border-radius: 8px;
        }

        .contacts-list::-webkit-scrollbar {
          width: 6px;
        }

        .contacts-list::-webkit-scrollbar-track {
          background: #f3f4f6;
          border-radius: 3px;
        }

        .contacts-list::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 3px;
        }

        .contacts-list::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }

        .contact-item:hover {
          border-color: #6366f1 !important;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
      `}</style>
    </div>
  );
};

export default ContactSelectionModal;